interface Edge {
  source: string;
  target: string;
}

export function getTopNodesOfGraph(nodes: string[], edges: Edge[]): string[] {
  if (!nodes?.length) return [];

  // Create a set of node IDs for quick lookup
  const nodeIds = new Set(nodes);

  // Create a map to store node connections
  const nodeConnections = new Map(nodes.map((node) => [node, { in: new Set<string>(), out: new Set<string>() }]));

  // Populate the nodeConnections map with edge information
  for (const edge of edges) {
    if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
      nodeConnections.get(edge.source)!.out.add(edge.target);
      nodeConnections.get(edge.target)!.in.add(edge.source);
    }
  }

  // Find top nodes (nodes with no incoming edges from the provided set of nodes)
  // Return the results
  return nodes.filter((node) => {
    const connections = nodeConnections.get(node)!;
    return connections.in.size === 0;
  });
}

export function getDownstreamNodes(nodeIds: string | string[], edges: Edge[]): string[] {
  const result = new Set<string>();
  const queue: string[] = Array.isArray(nodeIds) ? [...nodeIds] : [nodeIds];

  for (const nodeId of queue) {
    result.add(nodeId);
  }

  while (queue.length > 0) {
    const currentNode = queue.shift()!;
    const children = edges.filter((edge) => edge.source === currentNode).map((edge) => edge.target);

    for (const child of children) {
      if (!result.has(child)) {
        result.add(child);
        queue.push(child);
      }
    }
  }

  return [...result];
}

export function getUpstreamNodes(nodeId: string, edges: Edge[]): string[] {
  const connections = new Map<string, { source: string; target: string }[]>();

  // Group connections by target
  for (const connection of edges) {
    if (!connections.has(connection.target)) {
      connections.set(connection.target, []);
    }
    connections.get(connection.target)!.push(connection);
  }

  const upstreamNodes = new Set<string>([nodeId]);
  const toVisit: string[] = [nodeId];

  while (toVisit.length > 0) {
    const currentNode = toVisit.shift()!;
    if (connections.has(currentNode)) {
      for (const connection of connections.get(currentNode)!) {
        const source = connection.source;
        if (!upstreamNodes.has(source)) {
          toVisit.push(source);
        }
        upstreamNodes.add(source);
      }
    }
  }

  return [...upstreamNodes];
}
